<template>
  <div class="partnerSelect" :class="{ 'partnerSelect--embedded': embedded }">
    <!-- 고객사 -->
    <span v-if="!embedded">{{ $t('component.partnerCompany') }} : </span>
    <select
      v-model="partnerId"
      @change="selectPartner"
      :class="embedded ? ['rd-filter-select', 'rd-filter-select--md'] : ['partner-dropdown']"
    >
      <option value="">{{ $t('search.all') }}</option>
      <option v-for="item in partnerList" :value="item.partnerId" :key="item.partnerId">
        {{ item.partnerName }}
      </option>
    </select>
  </div>
</template>

<script>
import store from '@/store'
import { getAllPartnerList } from '@/api/domain/partner'

/**
 * @component PartnerSelect
 * @description 고객사(파트너) 선택 셀렉트박스 컴포넌트.
 * 마운트 시 전체 파트너 목록을 API로 조회합니다.
 * 선택 시 Vuex 스토어의 partnerId를 갱신하고 select-partner 이벤트를 emit합니다.
 * embedded=true 인 경우 자체 라벨을 숨기고 부모의 rd-filter-group 레이아웃에 녹아들도록 display: contents 를 적용합니다.
 */
export default {
  emits: ['select-partner'],
  props: {
    /**
     * 필터바(rd-compact-filter-bar) 내부에 배치될 때 true.
     * true 인 경우 자체 "고객사 :" 라벨을 렌더하지 않고, 셀렉트에 rd-filter-select 스타일을 적용합니다.
     */
    embedded: { type: Boolean, default: false }
  },
  // ── 반응형 데이터 ──
  data() {
    return {
      /** 파트너(고객사) 목록 */
      partnerList: [],
      /** 현재 선택된 파트너 ID (로그인 사용자의 partnerId로 초기화) */
      partnerId: this.$store.state.auth.user.id.partnerId || ''
    }
  },
  // ── watch ──
  watch: {
    /**
     * 다른 곳에서 store 의 partnerId 를 바꾸면 선택 표시도 따라간다 (SURINP-835).
     * 헤더 선택 상자는 router-view 보다 먼저 만들어져, 대시보드 카드로 들어온 접수 이력이 넣은 고객사를 못 보던 것.
     */
    '$store.state.auth.user.id.partnerId'(newId) {
      this.partnerId = newId || ''
    }
  },
  mounted() {
    this.selectBoxData()
  },
  // ── 메서드 ──
  methods: {
    /**
     * 전체 파트너 목록을 API에서 조회하여 드롭다운 옵션을 초기화합니다.
     */
    async selectBoxData() {
      // 모든 파트너 list 조회 api
      try {
        this.partnerList = await getAllPartnerList()
      } catch (error) {
        window.__APP_LOG__(error)
      }
    },
    /**
     * 파트너 선택 시 Vuex 스토어를 갱신하고 select-partner 이벤트를 emit합니다.
     */
    selectPartner() {
      store.commit('setPartnerId', this.partnerId)
      this.$emit('select-partner', this.partnerId)
    }
  }
}
</script>

<style scoped>
/* --- 파트너 셀렉트 컨테이너 레이아웃 --- */
.partnerSelect {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
}

.partnerSelect span {
  font-size: var(--font-body-sm, 0.8125rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--color-gray-600, #424750);
  white-space: nowrap;
}

.partner-dropdown {
  width: 76%;
  height: 32px;
  line-height: 32px;
  padding: 0 var(--space-2, 8px);
  border: 1px solid var(--color-gray-200, #B8BCC4);
  border-radius: var(--radius-sm, 4px);
  font-size: var(--font-body-sm, 0.8125rem);
  color: var(--color-gray-600, #424750);
  background: var(--color-white, #FFFFFF);
  transition: border-color var(--transition-fast, 0.15s ease);
  box-sizing: border-box;
}

.partner-dropdown:focus {
  border-color: var(--color-primary-500, #3B5998);
  box-shadow: var(--shadow-focus, 0 0 0 3px rgba(59, 89, 152, 0.15));
  outline: none;
}

/* --- 모바일 반응형 --- */
@media (max-width: 767px) {
  .partnerSelect {
    width: 100%;
    padding: 10px 14px;
    background: var(--color-gray-50, #F2F3F5);
    border-radius: var(--radius-md, 8px);
  }
  .partnerSelect span {
    flex-shrink: 0;
    font-size: var(--font-body-sm, 0.8125rem);
  }
  .partnerSelect select.partner-dropdown {
    flex: 1;
    width: 100% !important;
    min-width: 0;
    height: 40px;
    padding: 0 10px;
    border: 1px solid var(--color-gray-200, #B8BCC4);
    border-radius: var(--radius-md, 6px);
    background: var(--color-white, #FFFFFF);
  }
}
</style>
